import React, { memo, Fragment } from 'react';
import { List, ButtonBase, ListItem, Grid, Typography, Divider, Box } from '@material-ui/core';
import { fade } from '@material-ui/core/styles';
import { ProgramManagementProgram } from '../__generated__/ProgramManagementProgram';
import { ProgramManagementProgramGroup } from '../__generated__/ProgramManagementProgramGroup';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { useProgramManagementContext } from '../ProgramManagementContext';

interface Props {
  programs?: ProgramManagementProgram[];
  groups?: ProgramManagementProgramGroup[];
}

const ProgramItems = ({ programs }: { programs: ProgramManagementProgram[] }) => {
  const { theme } = useCampfireTheme();
  const { selectedProgramId, setSelectedProgramId } = useProgramManagementContext();

  return (
    <List style={{ padding: 0 }}>
      {programs.map((program, index) => (
        <Fragment key={program.programId}>
          <ButtonBase
            data-track='fs-prgMan-program-list-item'
            onClick={() => setSelectedProgramId(program.programId)}
            style={{ width: '100%' }}
          >
            <ListItem
              style={{ backgroundColor: selectedProgramId === program.programId ? theme.color.grey.border : undefined }}
            >
              <Grid container direction='column' style={{ paddingLeft: 24, paddingTop: 8, paddingBottom: 8 }}>
                <Grid item container alignItems='center' spacing={2}>
                  <Grid item style={{ padding: '4px 8px' }}>
                    <Typography variant='subtitle2' display='inline' style={{ fontWeight: 'bold' }}>
                      {program.name}
                    </Typography>
                  </Grid>
                  {program.isNew ? (
                    <Grid item style={{ padding: '4px 8px' }}>
                      <Box
                        style={{
                          backgroundColor: fade(theme.palette.primary.light, 0.4),
                          borderRadius: 2,
                          padding: '0px 4px',
                        }}
                      >
                        <Typography variant='caption' color='primary'>
                          {'New'}
                        </Typography>
                      </Box>
                    </Grid>
                  ) : null}
                </Grid>
              </Grid>
            </ListItem>
          </ButtonBase>
          {index < programs.length - 1 ? <Divider /> : null}
        </Fragment>
      ))}
    </List>
  );
};

const ProgramsList = memo((props: Props) => {
  const { programs, groups } = props;
  const { theme } = useCampfireTheme();

  if (!programs || programs.length === 0) return null;

  const groupIds = new Set((groups ?? []).map((group) => group.programGroupId));
  const sections = (groups ?? [])
    .map((group) => ({
      key: group.programGroupId,
      heading: group.name,
      programs: programs.filter((program) => program.programGroup?.programGroupId === group.programGroupId),
    }))
    .filter((section) => section.programs.length > 0);

  if (sections.length === 0) return <ProgramItems programs={programs} />;

  const ungrouped = programs.filter(
    (program) => !program.programGroup || !groupIds.has(program.programGroup.programGroupId)
  );
  if (ungrouped.length > 0) sections.push({ key: 'ungrouped', heading: 'Ungrouped', programs: ungrouped });

  return (
    <>
      {sections.map((section) => (
        <Box key={section.key}>
          <Box style={{ padding: '16px 24px 4px 40px' }}>
            <Typography style={{ fontSize: 13, fontWeight: 600, color: theme.color.grey.neutral500 }}>
              {section.heading}
            </Typography>
          </Box>
          <ProgramItems programs={section.programs} />
        </Box>
      ))}
    </>
  );
});

export { ProgramsList };
